
import React, { PropsWithChildren, useContext, useEffect } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './FooterComponent.css';
import { SpeedbumpContext } from '../../providers/Speedbump/Speedbump';
import { ModalContext } from '../../providers/Modal/Modal';
import { speedbumpModal } from '../LinkWrapper/LinkWrapper';
import { useWidthContext } from '../../providers/Width';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { useResizeHandler } from '../../hooks/useResizeHandler';
import withAsyncImport from '../../withAsyncImport';
import { hasDefaultComponent } from '../../utils/pageModelUtils';
const Footer = withAsyncImport(() => import('./components/Footer'));

interface IBottomLink {
    linkIconType: string;
    linkTitle: string;
    link: string;
}

interface ISocialIcons {
    iconPath: string;
    iconAltText: string;
    link: string;
}

interface IHamItems {
    hamburgerTitle: string;
    hamburgerLink: string;
    hamburgerParams?: string;
}

interface IIcon {
    iconPath: string;
    iconAltText: string;
}

interface IFooterComponentProps extends MappedComponentProperties {
    className?: string;
    copyrightMsg?: string;
    hideHorizontalLine?: boolean;

    phoneImgLabel?: IIcon;
    phoneLinkText?: string;
    phoneLink?: string;

    groupTitle1?: string;
    groupTitle2?:string;
    groupTitle3?:string;
    groupTitle4?:string;

    hamburgerItemsContainer1?:IHamItems[];
    hamburgerItemsContainer2?:IHamItems[];
    hamburgerItemsContainer3?:IHamItems[];
    hamburgerItemsContainer4?:IHamItems[];

    bottomLinks?: IBottomLink[];
    socialFields?: ISocialIcons[];

    pdfAltText?:string;
    lockAltText?: string;
}

export const FooterComponentEditConfig = {
    emptyLabel: 'FooterComponent',
    isEmpty: (props: PropsWithChildren<IFooterComponentProps>) => {
        return !props || !props.copyrightMsg;
    },
};

const FooterComponent = (props: PropsWithChildren<IFooterComponentProps>): JSX.Element => {
    const { title, content } = useContext(SpeedbumpContext);
    const { toggleModal, setModalContent, setModalClass } = useContext(ModalContext);
    const { width } = useWidthContext();

    function getHamburgerTitles(map?: IHamItems[], title?: string)  {
        const links: any[] = [];
        if (!map || !title) {
            return;
        }
        map.forEach(item => {
            const params = item.hamburgerParams?.startsWith('?') ? item.hamburgerParams : '?' + item.hamburgerParams;
            const path = item.hamburgerParams ? item.hamburgerLink + params : item.hamburgerLink;
            const obj = {
                label: item.hamburgerTitle,
                path: path,
            };
            links.push(obj);
        });
        const column = {
            title: title,
            links: links,
        };
        return column;
    }

    function getFooterMobileInfo() {
        if (!props.phoneImgLabel || props.phoneImgLabel.iconPath === '') {
            return undefined;
        }
        const obj = {
            icon: {
                src: props.phoneImgLabel.iconPath,
                alt: props.phoneImgLabel.iconAltText,
            },
            label: props.phoneLinkText ? props.phoneLinkText :'',
            link: props.phoneLink ? props.phoneLink: '',
        };
        return obj;
    }

    function getBottomLinkText(map: IBottomLink[] | undefined) {
        const linkTitles: any[] = [];
        if (map !== undefined) {
            map.forEach(item => {
                const obj = {
                    icon: item.linkIconType,
                    label: item.linkTitle,
                    link: item.link,
                };
                linkTitles.push(obj);
            });
        }
        return linkTitles;
    }

    function getSocialFields(map: ISocialIcons[] | undefined) {
        const socials: any[] = [];
        if (!map) {
            return;
        }
        map.forEach(item => {
            const obj = {
                src: item.iconPath,
                alt: item.iconAltText,
                link: item.link,
                onClick: (e: Event) => {
                    e.preventDefault();
                    setModalContent(speedbumpModal(toggleModal, width, undefined, item.link, title || '', content || ''));
                    toggleModal();
                },
            };
            socials.push(obj);
        });
        return socials;
    }

    useEffect(() => {
        setModalClass('Speedbump');
    }, []);

    const currentYear = new Date().getFullYear();
    useResizeHandler('.Footer', [document.body.scrollHeight, window.innerHeight]);

    return (
        <Footer id='footer-component' className={`${props.className || ''} Footer--hideOnPrint ${props.hideHorizontalLine ? 'Footer--hideHorizontalLine' : ''} ${hasDefaultComponent('cob-corp-acquisition/components/creditonebank/floating-cta') ? 'Footer__floatingCtaBuffer': ''}`}
            column1={getHamburgerTitles(props.hamburgerItemsContainer1, props.groupTitle1)}
            column2={getHamburgerTitles(props.hamburgerItemsContainer2, props.groupTitle2)}
            column3={getHamburgerTitles(props.hamburgerItemsContainer3, props.groupTitle3)}
            column4={getHamburgerTitles(props.hamburgerItemsContainer4, props.groupTitle4)}
            footerButton={getFooterMobileInfo()}
            socialIcons={getSocialFields(props.socialFields)}
            utilityLinks={getBottomLinkText(props.bottomLinks)}
            copyright={transformHTMLParserLinks(`© ${currentYear} ` + props.copyrightMsg)}
            pdfAltText={props.pdfAltText}
            lockAltText={props.lockAltText}>
        </Footer>
    );
};

export default FooterComponent;
